import React, { useEffect, useState } from 'react'
import {
  Box,
  MenuItem,
  Button,
  Typography,
  Divider,
  CircularProgress,
  Paper,
  Autocomplete,
  TextField,
} from '@mui/material'
import { Form } from 'react-final-form'
import { Select, Checkboxes } from 'mui-rff'
import styled from 'styled-components'
import { BasicTextField } from '../shared/component/BasicTextField'
import { permissions } from '../../constant/permission'
import { branch } from '../../constant/branch'
import { department } from '../../constant/department'
import { useCreateUser } from '../../api/user'
import { useEditUser } from '../../api/user'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { AdminXsMenu } from './AdminXsMenu'
import { useGetSalesIds } from '../../api/point'
import moment from 'moment'

const StyledFieldBox = styled(Box)`
  && {
    max-width: 500px;
    justify-content: space-evenly;
    min-height: 80px;
  }
`

export const UserForm = (props) => {
  const { title, curData } = props

  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const currentYear = new Date().getFullYear()
  const startDate = moment(new Date(currentYear, 0, 1)).format('YYYY-MM-DD') // 0 is january
  const endDate = moment(new Date(currentYear, 11, 31)).format('YYYY-MM-DD') // 11 is december

  const { data: salesIdsData = [], isLoading: salesIdsIsLoading } = useGetSalesIds({
    startDate,
    endDate,
  })

  const [salesIds, setSalesIds] = useState([])

  useEffect(() => {
    // Default to current data salesIds
    if (curData && curData.salesIds) {
      setSalesIds(curData.salesIds)
    }
  }, [curData])

  let permissionArray = []
  Object.keys(permissions).map((key) => {
    permissionArray.push({
      value: `${permissions[key]}`,
      label: `${permissions[key]}`,
    })
  })

  let branchArray = []
  {
    Object.keys(branch).map((key) => {
      branchArray.push({ value: key, label: branch[key] })
    })
  }

  const createUserApi = useCreateUser()
  const editUserApi = useEditUser()

  const onSubmit = async (values) => {
    // Append salesIds to formvalues
    const submitValues = { ...values, salesIds }

    if (curData) {
      editUserCall(submitValues)
    } else {
      createUserCall(submitValues)
    }
  }

  const createUserCall = async (values) => {
    const createRes = await createUserApi.mutateAsync(values).catch((e) => {
      const errMsg = e?.response?.data?.message
      enqueueSnackbar(errMsg && errMsg !== '' ? errMsg : 'Fail to create user', {
        variant: 'error',
      })
    })
    if (createRes.status === 200) {
      enqueueSnackbar('User Created', {
        variant: 'success',
      })
      navigate('/admin')
    }
  }
  const editUserCall = async (values) => {
    const editRes = await editUserApi
      .mutateAsync({ ...values, userId: values._id, salesIds: values?.salesIds ?? [] })
      .catch((e) => {
        enqueueSnackbar(e?.response?.data?.message ?? 'Fail to edit user', {
          variant: 'error',
        })
      })
    if (editRes.status === 200) {
      enqueueSnackbar('Edit user success', {
        variant: 'success',
      })
      navigate('/admin')
    }
  }

  const validate = (values) => {
    const errors = {}

    if (curData && !values.authId) {
      errors.authId = 'Required'
    }

    if (!values.email) {
      errors.email = 'Required'
    }

    if (!values.employeeId) {
      errors.employeeId = 'Required'
    }

    if (!values.position) {
      errors.position = 'Required'
    }

    if (!values.branch) {
      errors.branch = 'Required'
    }

    if (!values.department) {
      errors.department = 'Required'
    }

    return errors
  }

  const handleInitialValues = () => {
    if (curData) {
      return curData
    } else {
      return {}
    }
  }

  return (
    <Box sx={{ p: { xs: 3, sm: 5 } }} width='100%'>
      <Form
        onSubmit={onSubmit}
        validate={validate}
        keepDirtyOnReinitialize //let data stay on the form
        initialValues={handleInitialValues}
        component={({ handleSubmit, submitting, values, form }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <Box display='flex' flexDirection='row' justifyContent='space-between'>
                <Typography variant='h1'>{title}</Typography>
                <AdminXsMenu />
              </Box>
              <Paper style={{ padding: 16 }}>
                <Box display='flex' flexDirection='column' mt={3}>
                  {curData && (
                    <StyledFieldBox>
                      <BasicTextField
                        label='Auth ID'
                        fieldName='authId'
                        type='text'
                        disabled={curData ? true : false}
                      />
                    </StyledFieldBox>
                  )}

                  <StyledFieldBox>
                    <BasicTextField
                      label='Email'
                      fieldName='email'
                      required={true}
                      type='email'
                      disabled={curData ? true : false}
                    />
                  </StyledFieldBox>

                  <StyledFieldBox>
                    <BasicTextField
                      label='First Name'
                      fieldName='name'
                      disabled={curData ? true : false}
                    />
                  </StyledFieldBox>

                  <StyledFieldBox>
                    <BasicTextField
                      label='Last Name'
                      fieldName='lastName'
                      disabled={curData ? true : false}
                    />
                  </StyledFieldBox>

                  <StyledFieldBox>
                    <BasicTextField
                      label='Official Name'
                      fieldName='officialName'
                      disabled={curData ? true : false}
                    />
                  </StyledFieldBox>

                  <Divider />

                  <Box my={3}>
                    <Typography variant='h3'>Employee Details</Typography>
                  </Box>
                  <StyledFieldBox>
                    <BasicTextField label='Employee ID' fieldName='employeeId' required={true} />
                  </StyledFieldBox>

                  <StyledFieldBox>
                    <BasicTextField label='Position' fieldName='position' />
                  </StyledFieldBox>

                  <StyledFieldBox>
                    <Typography variant='h5'>Branch</Typography>
                    <Select
                      name='branch'
                      label=''
                      required={true}
                      formControlProps={{ margin: 'none' }}
                      size='small'
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: {
                              xs: '200px',
                            },
                          },
                        },
                      }}
                    >
                      {Object.keys(branch).map((key) => (
                        <MenuItem value={key} key={key}>
                          {branch[key]}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFieldBox>

                  <StyledFieldBox>
                    <Typography variant='h5'>Department</Typography>
                    <Select
                      name='department'
                      label=''
                      required={true}
                      formControlProps={{ margin: 'none' }}
                      size='small'
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: {
                              xs: '200px',
                            },
                          },
                        },
                      }}
                    >
                      {Object.keys(department).map((key) => (
                        <MenuItem value={key} key={key}>
                          {department[key]}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFieldBox>

                  <StyledFieldBox mb={3} sx={{ maxWidth: '600px' }}>
                    <Typography variant='h5'>Permission to access</Typography>
                    <Checkboxes
                      name='permission'
                      color='primary'
                      formControlProps={{ margin: 'none' }}
                      formGroupProps={{ row: true }}
                      data={permissionArray}
                    />
                  </StyledFieldBox>

                  <Divider />

                  <Box my={3}>
                    <Typography variant='h3'>Point System Settings</Typography>
                  </Box>

                  <StyledFieldBox maxHeight={60}>
                    <Checkboxes
                      name='isPointSystemAdmin'
                      color='primary'
                      formControlProps={{ margin: 'none' }}
                      formGroupProps={{ row: true }}
                      data={{ label: 'Is admin?' }}
                    />
                  </StyledFieldBox>

                  <StyledFieldBox>
                    <Typography variant='h5'>Sales IDs</Typography>
                    <Box display='flex' flexDirection='row' width='100%' alignItems='center'>
                      <Autocomplete
                        fullWidth
                        multiple
                        autoHighlight
                        name='salesIds'
                        options={salesIdsData}
                        onChange={(ev, val) => setSalesIds(val)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {salesIdsIsLoading ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    params.InputProps.endAdornment
                                  )}
                                </>
                              ),
                            }}
                          />
                        )}
                        value={salesIds}
                        disabled={salesIdsIsLoading}
                        loading={salesIdsIsLoading}
                      />
                    </Box>
                  </StyledFieldBox>
                </Box>

                <Box mt={3} width='100%' display='flex' justifyContent='flex-start'>
                  <Button
                    variant='contained'
                    type='submit'
                    disabled={submitting || editUserApi.isLoading || createUserApi.isLoading}
                  >
                    {(editUserApi.isLoading || createUserApi.isLoading) && (
                      <Box mr={1}>
                        <CircularProgress size='1.5rem' />
                      </Box>
                    )}
                    Submit
                  </Button>
                </Box>
              </Paper>
            </form>
          )
        }}
      />
    </Box>
  )
}

// const AuthIdListener = ({ values, onAuthIdChange, authIdData }) => {
//   const { change } = useForm()
//   useEffect(() => {
//     if (values?.authId) onAuthIdChange(values?.authId)
//   }, [values?.authId])

//   useEffect(() => {
//     if (authIdData) {
//       setTimeout(() => {
//         change('name', authIdData.name)
//         change('email', authIdData.email)
//       })
//     } else {
//       setTimeout(() => {
//         change('name', undefined)
//         change('email', undefined)
//       })
//     }
//   }, [authIdData])

//   return null
// }
